/* eslint-disable no-case-declarations */
/* eslint-disable @typescript-eslint/no-use-before-define */
import { useQuery } from '@apollo/client';
import BaseStructure from 'components/BaseStructure';
import SearchBarSuggestion from 'components/common/SearchBarSuggestion';
import { NOTIFY } from 'constants/notify';
import useCurrentLoggedInUser from 'hooks/apollo/queries/useCurrentLoggedInUser';
import { useQueryParamsAsKeyValuePair } from 'hooks/useQueryParams';
import { useRoutesDisplayValues } from 'hooks/useRoutesDisplayValues';
import { isBoolean } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { INotification } from 'redux/notification/types';
import { SearchScopeEnum } from 'redux/search/types';
import ROUTES from 'routes/routeConstants';
import { GET_APPOINTMENTS } from 'services/graphql/queries/appointment.query';
import { AppointmentStatus, IAppointment } from 'types/appointments';
import { IPagination } from 'types/common';
import { RoleEnum } from 'types/user';
import { checkRealtimeUpdate, notifyEvent } from 'utils/realtimeUpdate';

import AppointmentFilterBars from './components/AppointmentFilterBars';
import AppointmentsTabs from './components/AppointmentsTabs';

const Appointments: React.FC = () => {
  const { role: userRole } = useCurrentLoggedInUser(); // test nha
  const queryObject = useQueryParamsAsKeyValuePair();
  const tab = queryObject?.tab;
  // const path = useHistory().location.pathname;

  const appointmentTabsKeyByRole = useMemo(() => {
    switch (userRole) {
      case RoleEnum.ADMIN:
        return [
          { key: AppointmentStatus.APPROVED, label: 'Accepted' },
          { key: AppointmentStatus.REQUESTED, label: 'Requested' },
          { key: AppointmentStatus.OPEN, label: 'Unassigned' },
          { key: 'all', label: 'All' },
        ];
      case RoleEnum.CLIENT:
        return [
          { key: AppointmentStatus.APPROVED, label: 'Accepted' },
          { key: AppointmentStatus.REQUESTED, label: 'Requested' },
          { key: AppointmentStatus.OPEN, label: 'Unassigned' },
          { key: 'all', label: 'All' },
        ];
      default:
        return [];
    }
  }, [userRole]);

  const formattedQueryObject = queryObject
    ? Object.entries(queryObject).reduce((acc, [queryKey, queryValue]) => {
        const isStringifyObject = !isBoolean(queryValue) && queryValue?.includes('{');
        return {
          ...acc,
          [queryKey]: isStringifyObject ? JSON.parse(queryValue) : queryValue,
        };
      }, {} as any)
    : {};

  const filterStatus = useMemo(() => {
    if (!tab) return appointmentTabsKeyByRole[0]?.key;
    return tab;
  }, [queryObject]);

  // data for appointments
  const { data, refetch, loading } = useQuery<{ findAppointmentEntity: { data: IAppointment[]; meta: IPagination } }>(
    GET_APPOINTMENTS,
    {
      variables: {
        status: filterStatus === 'all' ? undefined : filterStatus,
        // ...queryVariables,
        ...formattedQueryObject,
        locationIds: formattedQueryObject.locationIds ? [formattedQueryObject.locationIds] : undefined,
        pagination: { page: parseFloat(queryObject?.page || '1') },
      },
    },
  );
  const appointments = data?.findAppointmentEntity?.data;
  const meta = data?.findAppointmentEntity?.meta;

  useEffect(() => {
    if (!loading && !!data) {
      refetch();
    }
  }, [queryObject]);

  // data for getting users price (Notary only)
  // const { data: transactionTypes } = useQuery<{
  //   findTransactionTypeEntity: { data: ITransactionType[] };
  // }>(findSpecificTransactionTypeByUser, {
  //   variables: {
  //     user: userId,
  //     pagination: { take: 50 },
  //   },
  //   skip: userRole !== RoleEnum.NOTARY,
  // });

  // const transactionTypesData = transactionTypes?.findTransactionTypeEntity?.data;

  const routeDisplayValue = useRoutesDisplayValues()[ROUTES.APPOINTMENTS];

  const handleSubscribeNotify = (subData: { newNotification: INotification }) => {
    if (checkRealtimeUpdate(subData.newNotification.subType)) {
      refetch();
    }
  };

  useEffect(() => {
    notifyEvent.addListener(NOTIFY.NEW, handleSubscribeNotify);
    return () => {
      notifyEvent.removeListener(NOTIFY.NEW, handleSubscribeNotify);
    };
  }, []);

  return (
    <BaseStructure
      mobileThreshold={575}
      header={routeDisplayValue.text}
      headerIconName={routeDisplayValue.iconClass}
      headerStyle={{ flexWrap: 'wrap' }}
      headerSide={
        <div className='gx-d-inline-block w-full' style={{ maxWidth: 400 }}>
          <SearchBarSuggestion
            placeholder='Search by User, Location, Company...'
            entities={[SearchScopeEnum.COMPANY, SearchScopeEnum.LOCATION, SearchScopeEnum.USER]}
          />
        </div>
      }
      headerBelow={userRole === RoleEnum.ADMIN || userRole === RoleEnum.CLIENT ? <AppointmentFilterBars /> : undefined}>
      <AppointmentsTabs
        appointmentTabsKeyByRole={appointmentTabsKeyByRole}
        meta={meta}
        appointments={appointments}
        refetch={refetch}
        loading={loading}
        // transactionTypesData={transactionTypesData}
      />
    </BaseStructure>
  );
};

export default React.memo(Appointments);
